import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {addWeeks, startOfWeek} from 'date-fns';
import {EmployeeRow} from './components/employee_row';
import {ColumnHeader} from './components/column_header';
import {Holiday} from '../models/holiday';
import {Employee} from '../models/employee';
import {Availability} from '../models/availability';
import {User} from '../models/user';
import {DestroyButton} from './components/destroy_button';
import {GridPresenter} from './grid_presenter';
import {PresenterProps, withPresenter} from './support/with_presenter';
import {classNames} from './support/classnames';

interface OwnProps {
    offset: number;
    holidays: Holiday[];
    user: User;
    employees: Employee[];
    availabilities: {[employee_id: number]: {[date: string]: Availability}};
}

const GridComponent: FunctionComponent<OwnProps & PresenterProps<GridPresenter>> = observer(
    ({presenter, holidays, user, employees}) => {
        const startOfCurrentWeek = addWeeks(startOfWeek(new Date(), {weekStartsOn: 1}), presenter.offset);
        const dates = Array.from({length: presenter.weeksInView}, (_, i) => i).map((n) =>
            addWeeks(startOfCurrentWeek, n),
        );
        const now = new Date();

        return (
            <div className={classNames('container-fluid', {compact: presenter.compact})} id="sprint-grid">
                <div className="row">
                    <div className="col-xs-12">
                        <div className="panel panel-default panel-extended panel-large">
                            <div className="panel-heading">
                                <h2>
                                    <a
                                        onClick={() => presenter.onBackNavigateClick()}
                                        className="ion-chevron-left padding-right"
                                    />
                                    Sprints
                                    <a
                                        onClick={() => presenter.onForwardNavigateClick()}
                                        className="ion-chevron-right padding-left"
                                    />
                                </h2>
                                <div className="options pull-left">
                                    <DestroyButton />
                                    <a
                                        onClick={() => presenter.handleCompactClick()}
                                        title={presenter.compact ? 'Zoom in' : 'Zoom out'}
                                        className={classNames(
                                            'btn btn-inline btn-tertiary btn-zoom',
                                            presenter.compact ? 'ion-android-contract' : 'ion-android-expand',
                                        )}
                                    />
                                </div>
                                <a href="/sprints/create" className="btn btn-inline ion-android-add">
                                    Add
                                </a>
                            </div>
                            <div className="panel-body panel-scrollable">
                                <table className="grid">
                                    <thead>
                                        <tr>
                                            <th>&nbsp;</th>
                                            {dates.map((date) => (
                                                <ColumnHeader
                                                    key={date.toDateString()}
                                                    now={now}
                                                    date={date}
                                                    compact={presenter.compact}
                                                    holidays={holidays}
                                                />
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {presenter.internalEmployees.map((employee) => (
                                            <EmployeeRow
                                                key={employee.id}
                                                now={now}
                                                compact={presenter.compact}
                                                user={user}
                                                employee={employee}
                                                dates={dates}
                                            />
                                        ))}
                                        <tr>
                                            <td className="spacer">
                                                <h4>External</h4>
                                            </td>
                                            {dates.map((date) => {
                                                return <td className="spacer">&nbsp;</td>;
                                            })}
                                        </tr>
                                        {presenter.externalEmployees.map((employee) => (
                                            <EmployeeRow
                                                key={employee.id}
                                                now={now}
                                                compact={presenter.compact}
                                                user={user}
                                                employee={employee}
                                                dates={dates}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

export const Grid = withPresenter<GridPresenter, OwnProps>(
    (props, component) => new GridPresenter(props.user, props.offset, props.employees, component.gridLoader),
    GridComponent,
);
